import React, { useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { HelpModeContext, CredsContext } from "./Contexts";
import Tools from "./JNSTools";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardPanelHeader from "./DashboardPanelHeader";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { CheckBoxComponent } from "@syncfusion/ej2-react-buttons";
import StatusRAIDComponent from "./StatusRAIDComponent";
import ChecklistIcon from "@mui/icons-material/Checklist";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
} from "@syncfusion/ej2-react-grids";
import "./styles/DashboardRAIDComponent.css";

function DashboardRAIDComponent(props) {
  const [displayPage, setDisplayPage] = useState();
  const helpMode = useContext(HelpModeContext).mode;
  const localWait = useRef(true);
  const [openDialog, setOpenDialog] = useState(false);
  const creds = useContext(CredsContext).creds;
  const localFinished = useRef(!props.finished);
  const oadaSettings = JSON.parse(localStorage.getItem("oadaSettings"));
  const projectObject = JSON.parse(sessionStorage.getItem("projectObject"));

  const OPEN_STAGE_ID = oadaSettings["openStageDropdown_options"];
  const ASSIGNED_STAGE_ID = oadaSettings["assignedStageDropdown_options"];
  const PENDING_STAGE_ID = oadaSettings["pendingStageDropdown_options"];
  const RESOLVED_STAGE_ID = oadaSettings["resolvedStageDropdown_options"];
  const DECISION_NEEDED_STAGE_ID =
    oadaSettings["decisionNeededStageDropdown_options"];
  const DECISION_MADE_STAGE_ID =
    oadaSettings["decisionMadeStageDropdown_options"];
  const HIGH_SEVERITY_ID = oadaSettings["highSeverityDropdown_options"];
  const MEDIUM_SEVERITY_ID = oadaSettings["mediumSeverityDropdown_options"];
  const LOW_SEVERITY_ID = oadaSettings["lowSeverityDropdown_options"];

  const stageOptions =
    props.raidType == "decision"
      ? [
          {
            name: "Awaiting Decision",
            id: DECISION_NEEDED_STAGE_ID,
            filterSetting: true,
          },
          {
            name: "Decision Made",
            id: DECISION_MADE_STAGE_ID,
            filterSetting: true,
          },
        ]
      : [
          { name: "Open", id: OPEN_STAGE_ID, filterSetting: true },
          { name: "Assigned", id: ASSIGNED_STAGE_ID, filterSetting: true },
          { name: "Pending", id: PENDING_STAGE_ID, filterSetting: true },
          { name: "Resolved", id: RESOLVED_STAGE_ID, filterSetting: true },
        ];

  const severityOptions = [
    { name: "High", id: HIGH_SEVERITY_ID, filterSetting: true },
    { name: "Medium", id: MEDIUM_SEVERITY_ID, filterSetting: true },
    { name: "Low", id: LOW_SEVERITY_ID, filterSetting: true },
    { name: "&lt;blank&gt;", id: -1, filterSetting: true },
  ];

  const stageFilterSettings =
    oadaSettings["raidStageFilterSettings_" + props.raidType];
  if (stageFilterSettings) {
    const stageFilters = JSON.parse(stageFilterSettings);
    for (let stg = 0; stg < stageOptions.length; stg++) {
      const stageFilter = stageFilters.find(
        (elem) => elem.id == stageOptions[stg].id,
      );
      if (stageFilter !== undefined) {
        stageOptions[stg].filterSetting = stageFilter.setting;
      }
    }
  }

  const severityFilterSettings =
    oadaSettings["raidSeverityFilterSettings_" + props.raidType];
  if (severityFilterSettings) {
    const severityFilters = JSON.parse(severityFilterSettings);
    for (let sev = 0; sev < severityOptions.length; sev++) {
      const severityFilter = severityFilters.find(
        (elem) => elem.id == severityOptions[sev].id,
      );
      if (severityFilter !== undefined) {
        severityOptions[sev].filterSetting = severityFilter.setting;
      }
    }
  }

  const filteredDataArray = [];
  if (props.dataArray) {
    for (let x = 0; x < props.dataArray.length; x++) {
      const stageFound = stageOptions.find(
        (elem) => elem.id == props.dataArray[x].issue_stage_id,
      ).filterSetting;
      const severityFound = props.dataArray[x].issue_severity_id
        ? severityOptions.find(
            (elem) => elem.id == props.dataArray[x].issue_severity_id,
          ).filterSetting
        : false;

      if (stageFound && severityFound) {
        filteredDataArray.push(props.dataArray[x]);
      }
    }
  }

  const handleCheckboxClick = (event) => {
    //console.log(event.target.id + " -- " + !event.target.checked);
    let stageFilters = stageFilterSettings
      ? JSON.parse(stageFilterSettings)
      : [];
    let severityFilters = severityFilterSettings
      ? JSON.parse(severityFilterSettings)
      : [];
    const elementId = event.target.id.substring(
      event.target.id.indexOf("_") + 1,
    );
    if (event.target.id.indexOf("Stage") >= 0) {
      const stageFilter = stageFilters.find((elem) => elem.id == elementId);
      if (stageFilter) {
        stageFilter.setting = !event.target.checked;
      } else {
        stageFilters.push({ id: elementId, setting: !event.target.checked });
      }

      oadaSettings["raidStageFilterSettings_" + props.raidType] =
        JSON.stringify(stageFilters);
      localStorage.setItem("oadaSettings", JSON.stringify(oadaSettings));
    }

    if (event.target.id.indexOf("Severity") >= 0) {
      const severityFilter = severityFilters.find(
        (elem) => elem.id == elementId,
      );
      if (severityFilter) {
        severityFilter.setting = !event.target.checked;
      } else {
        severityFilters.push({ id: elementId, setting: !event.target.checked });
      }

      oadaSettings["raidSeverityFilterSettings_" + props.raidType] =
        JSON.stringify(severityFilters);
      localStorage.setItem("oadaSettings", JSON.stringify(oadaSettings));
    }

    Tools.writeSettings(creds, projectObject.id);
  };

  const headerTemplate = (event) => {
    return <span className="dashHeaderRow">{event.headerText}</span>;
  };

  let toolTipAnimation = {
    open: { /*effect: "ZoomIn", duration: 1000,*/ delay: 1000 },
    close: { /*effect: "ZoomOut",*/ duration: 500, delay: 0 },
  };

  const handleOpen = (event) => {
    setOpenDialog(true);
  };

  const handleClose = (event) => {
    localFinished.current = true;
    setOpenDialog(false);
  };

  const configureComponent = (
    <ChecklistIcon
      id={"__dashboardRAIDConfigure_" + props.raidType}
      //onClick={helpMode ? null : handleOpen}
      onClick={handleOpen}
      fontSize="small"
      sx={{
        marginRight: "5px",
        cursor: helpMode ? "help" : "pointer",
      }}
    />
  );

  useEffect(() => {
    const prepDisplayPage = props.statusVersion ? (
      <StatusRAIDComponent
        dataArray={filteredDataArray}
        raidType={props.raidType}
      />
    ) : (
      <>
        <Box
          className="dashTableTitleBar"
          height="12%"
          id="__dashboardRAID_header"
          sx={{ cursor: helpMode ? "help" : "default" }}
        >
          <DashboardPanelHeader
            panelLocked={props.panelLocked}
            title={"RAID: " + props.raidType + "S"}
            removePanel={props.removePanel}
            panelId={props.panelId}
            headerComponent={configureComponent}
          />
        </Box>

        <Box
          height="85%"
          id={"__dashboardRAID_" + props.raidType}
          sx={{
            overflow: "auto",
            verticalAlign: "middle",
            border: "solid 2px",
            padding: "0px 4px 4px 4px",
            borderRadius: "0px 0px 6px 6px",
            backgroundColor: "white",
            position: "absolute",
            cursor: helpMode ? "help" : "default",
          }}
        >
          <TooltipComponent content="RAID items" animation={toolTipAnimation}>
            <Backdrop
              sx={{ color: "#fff", position: "absolute", zIndex: 10 }} //, zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={localWait.current}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            <GridComponent
              className="gridWithSmallHeader"
              enableStickyHeader={true}
              dataSource={filteredDataArray}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="name"
                  headerText="Name"
                  textAlign="Left"
                  width="15%"
                  headerTemplate={headerTemplate}
                  customAttributes={{ class: "dashTaskTitle" }}
                />
                <ColumnDirective
                  field="description"
                  headerText="Synopsis"
                  textAlign="Left"
                  width="55%"
                  headerTemplate={headerTemplate}
                  customAttributes={{ class: "dashTaskPercent" }}
                />
                <ColumnDirective
                  field="issue_stage_name"
                  headerText="Stage"
                  textAlign="Left"
                  width="15%"
                  headerTemplate={headerTemplate}
                  customAttributes={{ class: "dashTaskPercent" }}
                />
                <ColumnDirective
                  field="issue_severity_name"
                  headerText="Priority"
                  textAlign="Left"
                  width="15%"
                  headerTemplate={headerTemplate}
                  customAttributes={{ class: "dashTaskPercent" }}
                />
              </ColumnsDirective>
            </GridComponent>
          </TooltipComponent>
        </Box>
        <Dialog open={openDialog} onClose={helpMode ? null : handleClose}>
          <DialogTitle className="dashRAIDDialogTItle">Filter</DialogTitle>
          <Box
            sx={{
              backgroundColor: "gainsboro",
              padding: "10px",
              border: "solid 2px",
              width: "500px",
            }}
          >
            <div style={{ display: "flex" }}>
              <div
                style={{
                  width: "40%",
                }}
              >
                <Stack>
                  <div
                    style={{
                      padding: "2px 0px 10px 0px",
                      fontSize: "large",
                      fontWeight: "bold",
                    }}
                  >
                    Stage
                  </div>
                  {stageOptions.map((option, index) => (
                    <CheckBoxComponent
                      key={index}
                      id={"raidStageCheckbox_" + option.id}
                      label={option.name}
                      checked={option.filterSetting}
                      onClick={handleCheckboxClick}
                    />
                  ))}
                </Stack>
              </div>
              <div style={{ width: "40%" }}>
                <div
                  style={{
                    padding: "2px 0px 10px 0px",
                    fontSize: "large",
                    fontWeight: "bold",
                  }}
                >
                  Priority
                </div>
                <Stack>
                  {severityOptions.map((option, index) => (
                    <CheckBoxComponent
                      key={index}
                      id={"raidSeverityCheckbox_" + option.id}
                      label={option.name}
                      checked={option.filterSetting}
                      onClick={handleCheckboxClick}
                    />
                  ))}
                </Stack>
              </div>
            </div>
            <br></br>
            <br></br>
            <DialogActions sx={{ justifyContent: "center" }}>
              <Button
                className="dialogConfirmCancelButton"
                onClick={helpMode ? null : handleClose}
              >
                OK
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      </>
    );
    localWait.current = !localFinished.current;
    setDisplayPage(prepDisplayPage);
  }, [JSON.stringify(props.dataArray), openDialog, helpMode]);

  return displayPage;
}

export default DashboardRAIDComponent;

DashboardRAIDComponent.propTypes = {
  dataArray: PropTypes.array,
  individual: PropTypes.bool,
  inPanel: PropTypes.bool,
  panelLocked: PropTypes.bool,
  removePanel: PropTypes.func,
  panelId: PropTypes.number,
  finished: PropTypes.bool,
  initiateWait: PropTypes.bool,
  raidType: PropTypes.string,
  statusVersion: PropTypes.bool,
};
